/**
 * ProcessingStep - Background job progress tracking for Bank Entry workflow
 *
 * Polls job status and shows progress. User can safely navigate away.
 */

import React, { useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { useBankEntryStore } from '../../stores/bankEntryStore';
import { Loader2, Info } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL;
const POLL_INTERVAL = 5000; // 5 seconds

export const ProcessingStep: React.FC = () => {
  const token = localStorage.getItem('auth_token');
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    jobId,
    jobProgress,
    jobProgressMessage,
    setJobStatus,
    setCurrentStep,
    setResults,
    setError,
    rows,
  } = useBankEntryStore();

  // Poll for job status
  useEffect(() => {
    if (!jobId) {
      console.error('[ProcessingStep] No jobId found');
      setCurrentStep('preview');
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/jobs/${jobId}/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch job status: ${response.status}`);
        }

        const data = await response.json();

        // Update progress
        setJobStatus(data.progress || 0, data.progress_message || 'Processing...');

        // Check for completion
        if (data.status === 'completed') {
          console.log('[ProcessingStep] Job completed:', data.output_data);

          // Extract results from output_data
          const output = data.output_data || {};
          const results = (output.results || []).map((r: any) => ({
            rowIndex: r.row_index,
            status: r.status,
            voucherNumber: r.voucher_number,
            error: r.error,
          }));

          setResults(
            output.successCount || 0,
            output.failedCount || 0,
            output.paymentCount || 0,
            output.receiptCount || 0,
            output.contraCount || 0,
            results
          );

          setCurrentStep('complete');
          return;
        }

        if (data.status === 'failed') {
          console.error('[ProcessingStep] Job failed:', data.error_message);
          setError(data.error_message || 'Job failed');
          setResults(0, rows.length, 0, 0, 0, []);
          setCurrentStep('complete');
          return;
        }

        // Continue polling
        pollingRef.current = setTimeout(pollStatus, POLL_INTERVAL);
      } catch (error: any) {
        console.error('[ProcessingStep] Polling error:', error);
        // Continue polling despite errors
        pollingRef.current = setTimeout(pollStatus, POLL_INTERVAL);
      }
    };

    // Start polling
    pollStatus();

    // Cleanup on unmount
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
      }
    };
  }, [jobId, token, setJobStatus, setCurrentStep, setResults, setError, rows.length]);

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader className="text-center">
        <CardTitle className="flex items-center justify-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin" />
          Processing Vouchers
        </CardTitle>
        <CardDescription>
          Creating vouchers in Tally...
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Progress bar */}
        <div className="space-y-2">
          <Progress value={jobProgress} className="h-3" />
          <p className="text-sm text-center text-muted-foreground">
            {jobProgress}% complete
          </p>
        </div>

        {/* Status message */}
        <div className="text-center">
          <p className="text-lg font-medium">
            {jobProgressMessage || 'Starting...'}
          </p>
        </div>

        {/* Safe to leave notice */}
        <div className="flex items-center justify-center gap-2 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <Info className="w-5 h-5 text-blue-500" />
          <p className="text-sm text-blue-700 dark:text-blue-300">
            You can safely leave this page. Your vouchers will continue processing.
          </p>
        </div>

        {/* Job ID for reference */}
        <div className="text-center text-xs text-muted-foreground">
          Job ID: {jobId}
        </div>
      </CardContent>
    </Card>
  );
};

export default ProcessingStep;
